import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import './History.css';

const History = () => {
    const [history, setHistory] = useState([]);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        try {
            const historyData = JSON.parse(localStorage.getItem('customspro_classifications') || '{}');
            const saved = Array.isArray(historyData.classifications) ? [...historyData.classifications] : [];
            setHistory(saved.reverse());
        } catch (error) {
            console.error('Error loading history from localStorage:', error);
            setHistory([]);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const loadImageAsBase64 = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxDimension = 300;
                let { width, height } = img;
                if (width > height) {
                    if (width > maxDimension) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    }
                } else {
                    if (height > maxDimension) {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                // JPEG has no alpha — fill white so rounded logo corners aren't black
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                const dataURL = canvas.toDataURL('image/jpeg', 0.7);
                resolve({
                    dataURL,
                    width,
                    height,
                });
            };
            img.onerror = (error) => reject(error);
        });
    };

    const exportAllToPDF = async () => {
        if (history.length === 0) {
            alert('No classifications to export!');
            return;
        }

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const maxWidth = pageWidth - 2 * margin;
        let yPos = margin;

        // Helper function to check for page overflow and add new page if needed
        const checkPageOverflow = (requiredHeight) => {
            if (yPos + requiredHeight > pageHeight - margin) {
                doc.addPage();
                yPos = margin; // Reset yPos to top of new page
                // Add header on new pages
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(16);
                doc.setTextColor(0, 123, 255);
                doc.text('Customs Pro AI History Report (Continued)', margin, yPos);
                yPos += 10;
                doc.setLineWidth(0.5);
                doc.setDrawColor(0, 123, 255);
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 10;
            }
        };

        // Initial header
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(16);
        doc.setTextColor(0, 123, 255);
        doc.text('Customs Pro AI History Report', margin, yPos);
        yPos += 10;

        let calculatedHeight = 0;
        try {
            const logoSrc = require('../assets/logo.png');
            const { dataURL: logoBase64, width: logoWidth, height: logoHeight } = await loadImageAsBase64(logoSrc);
            const desiredWidth = 30;
            const aspectRatio = logoWidth / logoHeight;
            calculatedHeight = desiredWidth / aspectRatio;
            doc.addImage(logoBase64, 'JPEG', pageWidth - margin - desiredWidth, margin - 5, desiredWidth, calculatedHeight);
        } catch (error) {
            console.error('Logo load failed:', error);
        }

        const logoBottom = (margin - 5) + calculatedHeight;
        yPos = Math.max(yPos, logoBottom + 5);
        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 123, 255);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        history.forEach((item, index) => {
            // Classification Header
            checkPageOverflow(15); // Check for header and spacing
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'bold');
            doc.text(`Classification #${history.length - index} - ${item.timestamp}`, margin, yPos);
            yPos += 5;

            // Product Description
            doc.setFont('helvetica', 'normal');
            const descLines = doc.splitTextToSize(item.description || 'No description provided.', maxWidth);
            descLines.forEach((line) => {
                checkPageOverflow(5); // Check for each line
                doc.text(line, margin, yPos);
                yPos += 5;
            });
            yPos += 5;

            // Classification Fields
            const fields = [
                { label: 'HS Code', value: item.classification.hsCode + (item.classification.confidence ? ` (${item.classification.confidence})` : '') },
                { label: 'Stat Code', value: item.classification.statCode },
                { label: 'AHECC', value: item.classification.aheccCode },
                { label: 'Duty Rate', value: item.classification.dutyRate },
                { label: 'GST Exemption', value: item.classification.gstExemption },
            ];

            fields.forEach(({ label, value }) => {
                checkPageOverflow(5); // Check for each field line
                doc.setFont('helvetica', 'bold');
                doc.text(`${label}:`, margin, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(value || 'Unknown', margin + 30, yPos);
                yPos += 5;
            });

            // TCO Suggestions
            yPos += 5;
            checkPageOverflow(10); // Check for TCO Suggestions header
            doc.setFont('helvetica', 'bold');
            doc.text('TCO Suggestions:', margin, yPos);
            yPos += 5;
            doc.setFont('helvetica', 'normal');
            const tcoText = item.classification.tcoSuggestions
                ? item.classification.tcoSuggestions.map(t => t.tco === 'None' ? 'No TCOs applicable' : `${t.tco} - ${t.desc}`).join(', ')
                : 'Not classified yet';
            const tcoLines = doc.splitTextToSize(tcoText, maxWidth - 30);
            tcoLines.forEach((line) => {
                checkPageOverflow(5); // Check for each TCO line
                doc.text(line, margin + 5, yPos);
                yPos += 5;
            });

            // Explanation
            yPos += 5;
            checkPageOverflow(10); // Check for Explanation header
            doc.setFont('helvetica', 'bold');
            doc.text('Explanation:', margin, yPos);
            yPos += 5;
            doc.setFont('helvetica', 'normal');
            const explanationLines = doc.splitTextToSize(item.classification.explanation || 'No explanation available.', maxWidth);
            explanationLines.forEach((line) => {
                checkPageOverflow(5); // Check for each explanation line
                doc.text(line, margin, yPos);
                yPos += 5;
            });

            yPos += 10; // Extra spacing between classifications
        });

        // Footer (only on the last page)
        checkPageOverflow(10); // Ensure footer fits
        doc.setFontSize(8);
        doc.setTextColor(100);
        const timestamp = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Victoria' });
        doc.text(`Generated on: ${timestamp}`, margin, pageHeight - 10);

        doc.save(`Customspro_History_${Date.now()}.pdf`);
    };

    const exportSingleToPDF = async (item) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const maxWidth = pageWidth - 2 * margin;
        let yPos = margin;

        // Helper function to check for page overflow and add new page if needed
        const checkPageOverflow = (requiredHeight) => {
            if (yPos + requiredHeight > pageHeight - margin) {
                doc.addPage();
                yPos = margin; // Reset yPos to top of new page
                // Add header on new pages
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(16);
                doc.setTextColor(0, 123, 255);
                doc.text('Customs Pro AI Classification Report (Continued)', margin, yPos);
                yPos += 10;
                doc.setLineWidth(0.5);
                doc.setDrawColor(0, 123, 255);
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 10;
            }
        };

        // Initial header
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(16);
        doc.setTextColor(0, 123, 255);
        doc.text('Customs Pro AI Classification Report', margin, yPos);
        yPos += 10;

        let calculatedHeight = 0;
        try {
            const logoSrc = require('../assets/logo.png');
            const { dataURL: logoBase64, width: logoWidth, height: logoHeight } = await loadImageAsBase64(logoSrc);
            const desiredWidth = 30;
            const aspectRatio = logoWidth / logoHeight;
            calculatedHeight = desiredWidth / aspectRatio;
            doc.addImage(logoBase64, 'JPEG', pageWidth - margin - desiredWidth, margin - 5, desiredWidth, calculatedHeight);
        } catch (error) {
            console.error('Logo load failed:', error);
        }

        const logoBottom = (margin - 5) + calculatedHeight;
        yPos = Math.max(yPos, logoBottom + 5);
        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 123, 255);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`Classification - ${item.timestamp}`, margin, yPos);
        yPos += 5;

        // Product Description
        doc.setFont('helvetica', 'normal');
        const descLines = doc.splitTextToSize(item.description || 'No description provided.', maxWidth);
        descLines.forEach((line) => {
            checkPageOverflow(5); // Check for each line (5mm height per line)
            doc.text(line, margin, yPos);
            yPos += 5;
        });
        yPos += 5;

        // Classification Fields
        const fields = [
            { label: 'HS Code', value: item.classification.hsCode + (item.classification.confidence ? ` (${item.classification.confidence})` : '') },
            { label: 'Stat Code', value: item.classification.statCode },
            { label: 'AHECC', value: item.classification.aheccCode },
            { label: 'Duty Rate', value: item.classification.dutyRate },
            { label: 'Unit', value: item.classification.unit },
            { label: 'GST Exemption', value: item.classification.gstExemption },
        ];

        fields.forEach(({ label, value }) => {
            checkPageOverflow(5); // Check for each field line
            doc.setFont('helvetica', 'bold');
            doc.text(`${label}:`, margin, yPos);
            doc.setFont('helvetica', 'normal');
            doc.text(value || 'Unknown', margin + 30, yPos);
            yPos += 5;
        });

        yPos += 5;
        checkPageOverflow(10); // Check for TCO Suggestions header
        doc.setFont('helvetica', 'bold');
        doc.text('TCO Suggestions:', margin, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const tcoText = item.classification.tcoSuggestions
            ? item.classification.tcoSuggestions.map(t => t.tco === 'None' ? 'No TCOs applicable' : `${t.tco} - ${t.desc}`).join(', ')
            : 'Not classified yet';
        const tcoLines = doc.splitTextToSize(tcoText, maxWidth - 30);
        tcoLines.forEach((line) => {
            checkPageOverflow(5); // Check for each TCO line
            doc.text(line, margin + 5, yPos);
            yPos += 5;
        });

        yPos += 5;
        checkPageOverflow(10); // Check for Explanation header
        doc.setFont('helvetica', 'bold');
        doc.text('Explanation:', margin, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const explanationLines = doc.splitTextToSize(item.classification.explanation || 'No explanation available.', maxWidth);
        explanationLines.forEach((line) => {
            checkPageOverflow(5); // Check for each explanation line
            doc.text(line, margin, yPos);
            yPos += 5;
        });

        // Footer (only on the last page)
        checkPageOverflow(10); // Ensure footer fits
        doc.setFontSize(8);
        doc.setTextColor(100);
        const timestamp = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Victoria' });
        doc.text(`Generated on: ${timestamp}`, margin, pageHeight - 10);

        doc.save(`Classification_${item.classification.hsCode || 'Unknown'}_${Date.now()}.pdf`);
    };

    const deleteItem = (index) => {
        if (window.confirm('Are you sure you want to delete this classification?')) {
            const updatedHistory = [...history];
            updatedHistory.splice(index, 1);
            setHistory(updatedHistory);

            try {
                const historyData = JSON.parse(localStorage.getItem('customspro_classifications') || '{}');
                const saved = Array.isArray(historyData.classifications) ? [...historyData.classifications] : [];
                const originalIndex = saved.length - 1 - index;
                saved.splice(originalIndex, 1);
                const updatedData = {
                    classifications: saved,
                    expiry: historyData.expiry || Date.now() + 7 * 24 * 60 * 60 * 1000,
                };
                localStorage.setItem('customspro_classifications', JSON.stringify(updatedData));
            } catch (error) {
                console.error('Error deleting item from localStorage:', error);
                alert('Failed to delete item. Please try again.');
            }
        }
    };

    const handleLogout = () => {
        signOut(auth).then(() => {
            navigate('/');
        }).catch((error) => {
            console.error('Logout failed:', error);
        });
    };

    return (
        <div className="history-container">
            <header className="app-header">
                <img src={require('../assets/logo.png')} alt="Customs Pro AI Logo" className="app-logo" />
                <nav className="app-nav">
                    <Link to="/" className="nav-item">Home</Link>
                    <Link to="/app" className="nav-item">Classify Goods</Link>
                    <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                    <div
                        className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                        onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                        role="button"
                        aria-expanded={isProDropdownOpen}
                        aria-controls="pro-dropdown-menu"
                    >
                        <span className="active">Pro</span>
                        <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                            <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                            <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                            <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                            <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                            <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                            <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                            <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                            <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                            <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                            <Link to="/history" className="dropdown-item active" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                        </div>
                    </div>
                    <Link to="/settings" className="nav-item">Settings</Link>
                    <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                </nav>
            </header>

            <main className="history-main">
                <section className="history-panel">
                    <h2 className="panel-title">Classification History</h2>
                    <button className="action-btn export-all-btn" onClick={exportAllToPDF}>
                        Export All to PDF
                    </button>
                    {history.length === 0 ? (
                        <p className="no-history">No classifications saved yet.</p>
                    ) : (
                        <div className="history-list">
                            {history.map((item, index) => (
                                <div key={index} className="history-item">
                                    <div className="history-header">
                                        <span className="history-timestamp">{item.timestamp}</span>
                                        <span className="history-description">{item.description || 'No description'}</span>
                                    </div>
                                    <div className="history-details">
                                        <p><strong>HS Code:</strong> {item.classification.hsCode} {item.classification.confidence && `(${item.classification.confidence})`} <strong>Stat:</strong> {item.classification.statCode}</p>
                                        <p><strong>AHECC:</strong> {item.classification.aheccCode}</p>
                                        <p><strong>Duty Rate:</strong> {item.classification.dutyRate}</p>
                                        <p><strong>GST Exemption:</strong> {item.classification.gstExemption}</p>
                                        <p><strong>TCO:</strong> {item.classification.tcoSuggestions?.map(t => t.tco).join(', ') || 'None'}</p>
                                    </div>
                                    <div className="history-actions">
                                        <button className="action-btn export-btn" onClick={() => exportSingleToPDF(item)}>
                                            Export to PDF
                                        </button>
                                        <button className="action-btn delete-btn" onClick={() => deleteItem(index)}>
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default History;